import { useState } from 'react'
import { Checkbox } from '@/components/ui/checkbox'
import { INDUSTRIES_OF_INTEREST } from '@/constants'

interface IndustriesManagementProps {
  selectedIndustries: string[]
  onUpdate: (industries: string[]) => Promise<void>
  loading?: boolean
}

export const IndustriesManagement = ({
  selectedIndustries,
  onUpdate,
  loading = false
}: IndustriesManagementProps) => {
  const [isUpdating, setIsUpdating] = useState(false)

  const handleIndustryToggle = async (industry: string, checked: boolean) => {
    try {
      setIsUpdating(true)
      const updatedIndustries = checked
        ? [...selectedIndustries, industry]
        : selectedIndustries.filter(i => i !== industry)
      
      await onUpdate(updatedIndustries)
    } catch (error) {
      console.error('Failed to update industries:', error)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleSelectAll = async () => {
    try {
      setIsUpdating(true)
      await onUpdate([...INDUSTRIES_OF_INTEREST])
    } catch (error) {
      console.error('Failed to select all industries:', error)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleClearAll = async () => {
    try {
      setIsUpdating(true)
      await onUpdate([])
    } catch (error) {
      console.error('Failed to clear all industries:', error)
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-[var(--text)]">Industries of Interest</h3>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSelectAll}
            disabled={loading || isUpdating}
            className="text-xs text-black hover:text-gray-700 disabled:opacity-50"
          >
            Select All
          </button>
          <button
            type="button"
            onClick={handleClearAll}
            disabled={loading || isUpdating}
            className="text-xs text-black hover:text-gray-700 disabled:opacity-50"
          >
            Clear All
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto p-3 border border-border rounded-lg bg-[var(--background)]">
        {INDUSTRIES_OF_INTEREST.map((industry) => (
          <div key={industry} className="flex items-center gap-3">
            <Checkbox
              id={`industry-${industry}`}
              checked={selectedIndustries.includes(industry)}
              onCheckedChange={(checked) => handleIndustryToggle(industry, checked as boolean)}
              disabled={loading || isUpdating}
              className="data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
            />
            <label htmlFor={`industry-${industry}`} className="cursor-pointer">
              <span className="text-sm text-[var(--text)]">{industry}</span>
            </label>
          </div>
        ))}
      </div>

      {selectedIndustries.length > 0 && (
        <div className="text-xs text-[var(--text)] opacity-60">
          Selected: {selectedIndustries.length} of {INDUSTRIES_OF_INTEREST.length}
        </div>
      )}

      {selectedIndustries.length === 0 && (
        <p className="text-sm text-[var(--text)] opacity-60">
          No industries selected. Choose industries that interest you above.
        </p>
      )}
    </div>
  )
}
